import { StyledFunction } from 'styled-components'
import { Link } from 'react-router-dom'

import styled from '../../theme/styled'

import {
  SelectTheme,
  ButtonProps as ThemeButton
} from '../../theme/defaultTheme'

interface ButtonProps {
  primary?: boolean
  disabled?: boolean
  full?: boolean
}

export function styledComponentWithProps<
  T,
  U extends HTMLElement = HTMLElement
>(
  styledFunction: StyledFunction<React.HTMLProps<U>>
): StyledFunction<T & React.HTMLProps<U>> {
  return styledFunction
}

type Themed = { theme: SelectTheme }

/* These helpers allow us to access theme variables conveniently and type-safely.

  Compare:

  color: ({ theme, primary }) => primary
    ? theme.uiElements.primaryButton.color
    : theme.uiElements.button.hover.color

  vs

   color: ${buttonProp('color')};
*/
const buttonProp = (prop: keyof ThemeButton) => ({ theme, primary }) =>
  primary ? theme.uiElements.primaryButton[prop] : theme.uiElements.button[prop]

const primaryProp = (prop: keyof ThemeButton) => ({ theme }: Themed) =>
  theme.uiElements.primaryButton[prop]
const secondaryProp = (prop: keyof ThemeButton) => ({ theme }: Themed) =>
  theme.uiElements.button[prop]

export const LinkButton = styled(Link)`
  min-width: 200px;

  color: ${primaryProp('color')};
  background: ${primaryProp('background')};

  text-transform: uppercase;
  display: inline-block;
  opacity: 1;
  text-align: center;
  height: 56px;
  line-height: 56px;
  text-decoration: none;
  letter-spacing: 0.2px;
  font-weight: bold;

  &:disabled {
    opacity: 0.2;
  }

  &.secondary {
    /* TODO: work out styledComponentWIthProps for Link so we can have primary={true}
       analogous to regular buttons */
    background: ${secondaryProp('background')};
  }

  &:hover {
    background: ${primaryProp('hover')};
  }

  &.secondary:hover {
    background: ${secondaryProp('hover')};
  }
`

export const BaseButton = styledComponentWithProps<ButtonProps>(styled.button)`
  color: ${buttonProp('color')};
  background: ${buttonProp('background')};

  border: 0;
  min-width: 220px;

  letter-spacing: 0.2px;
  font-weight: bold;
  text-transform: uppercase;

  display: ${({ full }) => (full ? 'block' : 'inline')};
  width: ${({ full }) => (full ? '100%' : 'auto')};
  opacity: ${({ disabled }) => (disabled ? 0.2 : 1)};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};

  // outline: 0;

  &:hover {
    background: ${buttonProp('hover')};
  }

  &:disabled {
    background: ${buttonProp('background')};
  }

  &:active {
    transform: translate(0px, 4px)
  }

  a {
    text-decoration: none;
    color: ${primaryProp('color')};
    display: block;
    height: 100%;
    line-height: ${({ full }) => (full ? '45px' : '36px')};
  }
`

export const LargeButton = styled(BaseButton)`
  height: 62px;
  font-size: 20px;

  padding: 15px;
  font-weight: 600;
`

export const Button = styled(BaseButton)`
  height: ${({ full }) => (full ? '45px' : '36px')};
`

export const TextButton = styled.button`
  border: 0;
  background: 0;
  border-radius: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  color: ${({ theme }) => theme.colors.default};
  text-decoration: underline;
  font-size: ${({ theme }) => theme.fonts.defaultSize};
  padding: 0;
  cursor: pointer;
`

export const PrimaryTextButton = styled.button`
  border: 0;
  background: 0;
  border-radius: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  color: ${({ theme }) => theme.colors.brand};
  text-decoration: underline;
  font-size: ${({ theme }) => theme.fonts.defaultSize};
  padding: 0;
  cursor: pointer;
`

export const ButtonToolbar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  button,
  a {
    margin-right: 46px;
  }

  button:last-child,
  a:last-child {
    margin-right: 0;
  }
`

export const Form = styled.form`
  h4 {
    margin: 0 0 20px 0;
    font-weight: bold;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.light};
  }
`

export const FormSection = styled.div`
  margin-bottom: 40px;
`



// WEBPACK FOOTER //
// ./src/Components/Styled/Shared.tsx